import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Text,
  ThemeToggle,
  useTheme,
} from "@microbeta/ui";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MdFeedback, MdLogout, MdSettings, MdStar } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import config from "../../../../config";
import { useAuthContext } from "../../../../features/auth";
import { useUserProfile } from "../../../../features/auth/hooks/useUserProfile";
import { usePurchases } from "../../../../features/purchases";
import type { ImageItem } from "../../../../services/imageUtils";
import { useAnalytics } from "../../../../shared/hooks";
import { paths } from "../../../utils/routing";
import { ResponsiveImage } from "../../ResponsiveImage/ResponsiveImage";
import { LanguageDropdownItem } from "../../ui/LanguageSelector/LanguageDropdownItem";
import { NotificationDropdown } from "./NotificationDropdown";
import styles from "./navigation.module.scss";

interface NavigationProps {
  className?: string;
}

export const Navigation: React.FC<NavigationProps> = ({ className }) => {
  const { t } = useTranslation();
  const { authState, logout } = useAuthContext();
  const { data: userProfile } = useUserProfile();
  const { isPremium, getPurchaseLink } = usePurchases();
  const navigate = useNavigate();
  const { captureEvent } = useAnalytics();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  const handleLogout = async () => {
    const startTime = Date.now();

    captureEvent("profile_menu_logout_clicked", {
      user_id: authState.userId,
      has_profile: !!userProfile,
      display_name: userProfile?.display_name || authState.displayName,
    });

    await logout();

    const duration = Date.now() - startTime;
    captureEvent("user_logged_out", {
      method: "profile_menu",
      duration_ms: duration,
      had_profile: !!userProfile,
    });

    navigate(paths.signin);
  };

  const handleSubscriptionClick = () => {
    captureEvent("profile_menu_subscription_clicked", {
      user_id: authState.userId,
      is_premium: isPremium,
    });

    navigate(paths.subscription);
    setIsProfileMenuOpen(false);
  };

  const handleSettingsClick = () => {
    captureEvent("profile_menu_settings_clicked", {
      user_id: authState.userId,
      has_profile: !!userProfile,
      display_name: userProfile?.display_name || authState.displayName,
    });

    navigate(paths.settings);
    setIsProfileMenuOpen(false);
  };

  const handleFeedbackClick = () => {
    captureEvent("profile_menu_feedback_clicked", {
      user_id: authState.userId,
      has_profile: !!userProfile,
      display_name: userProfile?.display_name || authState.displayName,
    });

    navigate(paths.feedback);
    setIsProfileMenuOpen(false);
  };

  const handleProfileMenuToggle = (open: boolean) => {
    setIsProfileMenuOpen(open);

    if (open) {
      captureEvent("profile_menu_opened", {
        user_id: authState.userId,
        has_profile: !!userProfile,
        display_name: userProfile?.display_name || authState.displayName,
        has_avatar: !!userProfile?.profile_images,
      });
    } else {
      captureEvent("profile_menu_closed", {
        user_id: authState.userId,
        interaction_type: "click_outside", // Default assumption
      });
    }
  };

  const handleThemeToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    captureEvent("theme_toggle_clicked", {
      user_id: authState.userId,
      source: "profile_menu",
    });
    toggleTheme();
  };

  const getUserDisplayName = () => {
    return (
      userProfile?.display_name ||
      authState.displayName ||
      authState.email?.split("@")[0] ||
      t("navigation.user")
    );
  };

  const getUserInitial = () => {
    const name = getUserDisplayName();
    return name.charAt(0).toUpperCase();
  };

  const handlePurchaseClick = () => {
    const purchaseLink = getPurchaseLink();

    if (!purchaseLink) {
      console.error(
        "[Navigation] Purchase link not available. Please configure VITE_REVENUECAT_PURCHASE_LINK_URL"
      );
      return;
    }

    captureEvent("purchase_link_clicked", {
      user_id: authState.userId,
      is_premium: isPremium,
      source: "navigation_header",
    });

    // Open RevenueCat Web Purchase Link in new window
    window.open(purchaseLink, "_blank", "noopener,noreferrer");
  };

  return (
    <nav className={`${styles.navigation} ${className || ""}`}>
      <div className={styles.rightSection}>
        {config.FEATURE_FLAG_SHOW_PAYMENTS && !isPremium && (
          <Button
            variant="primary"
            size="small"
            onClick={handlePurchaseClick}
            className={styles.purchaseButton}
          >
            {t("navigation.upgrade", "Upgrade")}
          </Button>
        )}

        <div className={styles.desktopNotification}>
          <NotificationDropdown />
        </div>

        <DropdownMenu
          open={isProfileMenuOpen}
          onOpenChange={handleProfileMenuToggle}
        >
          <DropdownMenuTrigger asChild>
            <button type="button" className={styles.profileButton}>
              {userProfile?.profile_images ? (
                <ResponsiveImage
                  variants={userProfile.profile_images as unknown as ImageItem}
                  alt={getUserDisplayName()}
                  className={styles.avatarImage}
                  size="thumb"
                  sizes="32px"
                  priority={true}
                />
              ) : (
                <div className={styles.avatarPlaceholder}>
                  <Text variant="small" className={styles.avatarInitial}>
                    {getUserInitial()}
                  </Text>
                </div>
              )}
              <Text variant="medium" className={styles.userName}>
                {getUserDisplayName()}
              </Text>
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent className={styles.profileMenu} align="end">
            <div className={styles.profileMenuHeader}>
              <div className={styles.profileInfo}>
                <Text variant="medium" className={styles.profileName}>
                  {getUserDisplayName()}
                </Text>
                {userProfile?.handle && (
                  <Text variant="small" className={styles.profileHandle}>
                    @{userProfile.handle}
                  </Text>
                )}
                <Text variant="small" className={styles.profileEmail}>
                  {authState.email}
                </Text>
              </div>
            </div>

            <DropdownMenuItem
              className={`${styles.menuItem} ${styles.languageMenuItem}`}
            >
              <LanguageDropdownItem />
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleThemeToggle}
              className={styles.menuItem}
            >
              <ThemeToggle size="sm" />
              <Text variant="medium" className={styles.menuItemText}>
                {theme === "dark"
                  ? t("navigation.darkMode")
                  : t("navigation.lightMode")}
              </Text>
            </DropdownMenuItem>

            {config.FEATURE_FLAG_SHOW_PAYMENTS && (
              <DropdownMenuItem
                onClick={handleSubscriptionClick}
                className={styles.menuItem}
              >
                <MdStar className={styles.menuItemIcon} />
                <Text variant="medium" className={styles.menuItemText}>
                  {t("settings.subscription.title")}
                </Text>
              </DropdownMenuItem>
            )}

            <DropdownMenuItem
              onClick={handleSettingsClick}
              className={styles.menuItem}
            >
              <MdSettings className={styles.menuItemIcon} />
              <Text variant="medium" className={styles.menuItemText}>
                {t("navigation.settings")}
              </Text>
            </DropdownMenuItem>

            <DropdownMenuItem
              onClick={handleFeedbackClick}
              className={styles.menuItem}
            >
              <MdFeedback className={styles.menuItemIcon} />
              <Text variant="medium" className={styles.menuItemText}>
                {t("navigation.feedback")}
              </Text>
            </DropdownMenuItem>

            <DropdownMenuItem
              onClick={handleLogout}
              className={styles.menuItem}
            >
              <MdLogout className={styles.menuItemIcon} />
              <Text
                variant="medium"
                className={`${styles.menuItemText} ${styles.logoutText}`}
              >
                {t("rootLayout.logout")}
              </Text>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </nav>
  );
};
